/**
 * Authentication helpers — token management.
 *
 * Access tokens are kept in memory (_memoryToken) so they are never exposed
 * to XSS via localStorage. The localStorage fallback preserves compatibility
 * with existing cert-login tokens.
 */

let _memoryToken: string | null = null;

export const setInMemoryToken = (t: string | null): void => {
  _memoryToken = t;
};

export function setAuthToken(token: string | null): void {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
}

export function getAuthToken(): string | null {
  return _memoryToken ?? localStorage.getItem('authToken');
}

/** JWT `sub` (user uuid) — used to scope per-user client storage. */
export function getUserIdFromAccessToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized)) as { sub?: unknown };
    return typeof decoded.sub === 'string' && decoded.sub ? decoded.sub : null;
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  _memoryToken = null;
  localStorage.removeItem('authToken');
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}
